import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'sonner'
import {
  syncQueueService,
  type SyncEntity,
  type SyncOperation,
  type SyncQueueItem,
} from '../../../features/sync/services/syncQueue'
import { logout } from './authSlice'

/**
 * Serializable summary of a dead-lettered queue item for the sync issues
 * panel. The raw item's `data` payload (which can hold File blobs) stays in
 * IndexedDB and out of Redux.
 */
export interface FailedSyncItem {
  id: string
  operation: SyncOperation
  entity: SyncEntity
  entityId: string
  timestamp: number
  retryCount: number
  error?: string
}

export interface SyncState {
  isOnline: boolean
  isSyncing: boolean
  lastSyncTime: number | null
  /** Bumped only when a sync run actually pushed changes to the server —
   * views watch this to refetch lists without churning on empty runs */
  lastChangeTime: number | null
  pendingCount: number
  failedCount: number
  failedItems: FailedSyncItem[]
  error: string | null
}

const initialState: SyncState = {
  isOnline: navigator.onLine,
  isSyncing: false,
  lastSyncTime: null,
  lastChangeTime: null,
  pendingCount: 0,
  failedCount: 0,
  failedItems: [],
  error: null,
}

const toFailedSummary = (item: SyncQueueItem): FailedSyncItem => ({
  id: item.id,
  operation: item.operation,
  entity: item.entity,
  entityId: item.entityId,
  timestamp: item.timestamp,
  retryCount: item.retryCount,
  error: item.error,
})

const getQueueSnapshot = async () => ({
  stats: await syncQueueService.getQueueStats(),
  failedItems: (await syncQueueService.getFailedItems()).map(toFailedSummary),
})

/**
 * Async thunk to process sync queue. The condition guard keeps the
 * periodic drain, the 'online' event, and manual sync from overlapping.
 */
export const processSync = createAsyncThunk(
  'sync/processQueue',
  async (_, { rejectWithValue }) => {
    try {
      const result = await syncQueueService.processQueue()

      if (result.deadLettered > 0) {
        toast.error(
          `${result.deadLettered} ${result.deadLettered === 1 ? 'change' : 'changes'} failed to sync`,
          {
            id: 'sync-dead-letter',
            description: 'Nothing was lost — open the sync status panel to retry or discard.',
          }
        )
      }

      return { ...result, ...(await getQueueSnapshot()) }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Sync failed')
    }
  },
  {
    condition: (_, { getState }) => !(getState() as { sync: SyncState }).sync.isSyncing,
  }
)

/**
 * Async thunk to get queue statistics and failed items
 */
export const getQueueStats = createAsyncThunk('sync/getQueueStats', async () => {
  return await getQueueSnapshot()
})

/**
 * Async thunk to manually retry one dead-lettered item
 */
export const retryFailedItem = createAsyncThunk('sync/retryFailedItem', async (id: string) => {
  try {
    await syncQueueService.retryFailedItem(id)
    toast.success('Change synced')
  } catch {
    toast.error('Retry failed — the change is queued for another attempt')
  }
  return await getQueueSnapshot()
})

/**
 * Async thunk to permanently discard one dead-lettered item
 */
export const discardFailedItem = createAsyncThunk('sync/discardFailedItem', async (id: string) => {
  await syncQueueService.discardFailedItem(id)
  return await getQueueSnapshot()
})

/**
 * Async thunk to clear failed items
 */
export const clearFailedItems = createAsyncThunk('sync/clearFailedItems', async () => {
  await syncQueueService.clearFailedItems()
  return await getQueueSnapshot()
})

const syncSlice = createSlice({
  name: 'sync',
  initialState,
  reducers: {
    /**
     * Set online status
     */
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload
    },
    /**
     * Increment pending count (when adding to queue)
     */
    incrementPending: (state) => {
      state.pendingCount++
    },
    /**
     * Surface a background sync failure (stale-cache refresh, etc.)
     */
    setSyncError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    /**
     * Clear sync error
     */
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    const applySnapshot = (
      state: SyncState,
      action: PayloadAction<{
        stats: { pending: number; inProgress: number; failed: number }
        failedItems: FailedSyncItem[]
      }>
    ) => {
      state.pendingCount = action.payload.stats.pending
      state.failedCount = action.payload.stats.failed
      state.failedItems = action.payload.failedItems
    }

    builder
      // Process sync
      .addCase(processSync.pending, (state) => {
        state.isSyncing = true
        state.error = null
      })
      .addCase(processSync.fulfilled, (state, action) => {
        state.isSyncing = false
        state.lastSyncTime = Date.now()
        if (action.payload.success > 0) {
          state.lastChangeTime = Date.now()
        }
        applySnapshot(state, action)
      })
      .addCase(processSync.rejected, (state, action) => {
        state.isSyncing = false
        state.error = action.payload as string
      })
      // Queue snapshots
      .addCase(getQueueStats.fulfilled, applySnapshot)
      .addCase(retryFailedItem.fulfilled, applySnapshot)
      .addCase(discardFailedItem.fulfilled, applySnapshot)
      .addCase(clearFailedItems.fulfilled, applySnapshot)
      // Clear sync state on logout
      .addCase(logout.fulfilled, (state) => {
        state.isSyncing = false
        state.lastSyncTime = null
        state.lastChangeTime = null
        state.pendingCount = 0
        state.failedCount = 0
        state.failedItems = []
        state.error = null
        // Note: isOnline is kept as it reflects actual network status
      })
  },
})

export const { setOnlineStatus, incrementPending, setSyncError, clearError } = syncSlice.actions
export default syncSlice.reducer
